// src/lib/session-policy.ts
//
// Single source of truth for session/inactivity behavior, shared between
// AuthProvider (silent token refresh) and InactivityLogout (idle timer +
// JWT-expiry warning). Previously these two lived apart and quietly drifted:
// AuthProvider tracked a narrower set of "activity" events than
// InactivityLogout, and refreshed on a flat 10-minute poll instead of a
// schedule tied to the token's real expiry — together that let a genuinely
// active user (e.g. scrolling a long report) get force-logged-out by the
// hard JWT deadline before the next refresh check ever ran. Keeping both
// constants here means the two mechanisms can't disagree again.
//
// All timings below are configurable via the frontend .env files (VITE_
// prefix, see .env / .env.production) instead of being hard-coded, so they
// can be tuned without a code change. Each falls back to its previous
// hard-coded value if the env var is absent, blank, or not a positive number.

function minutesFromEnv(key: string, fallbackMinutes: number): number {
  const raw = (import.meta.env as Record<string, string | undefined>)[key];
  const parsed = raw !== undefined && raw !== "" ? Number(raw) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallbackMinutes;
}

// Events that count as "the user is actively using the app." This list must
// stay identical everywhere activity is tracked.
export const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "click",
  "scroll",
  "touchstart",
  "pointermove",
] as const;

// Idle window that triggers a genuine inactivity logout (InactivityLogout's
// idle timer). Should stay comfortably under the backend JWT TTL (see
// app.env.prod / .env's APP_SECURITY_JWT_ACCESS_TOKEN_TTL_SECONDS) so it's
// this idle timer -- not a token silently expiring under an active user --
// that decides an inactivity logout.
export const INACTIVITY_LIMIT_MS =
  minutesFromEnv("VITE_SESSION_INACTIVITY_LIMIT_MINUTES", 14) * 60 * 1000;

// How long before the JWT actually expires AuthProvider should attempt a
// silent refresh, provided the user has been active within
// INACTIVITY_LIMIT_MS. Generous enough that a successful refresh happens
// well before InactivityLogout's own "session expiring" warning would show.
export const REFRESH_BEFORE_EXPIRY_MS =
  minutesFromEnv("VITE_SESSION_REFRESH_BEFORE_EXPIRY_MINUTES", 5) * 60 * 1000;

// How long before the idle timeout fires InactivityLogout shows its
// "you're about to be logged out" warning.
export const IDLE_WARN_BEFORE_MS =
  minutesFromEnv("VITE_SESSION_IDLE_WARN_BEFORE_MINUTES", 2) * 60 * 1000;

// How long before the JWT actually expires InactivityLogout shows its
// "your session is expiring" warning (distinct from the idle warning above --
// this one fires even if the silent refresh itself fails).
export const JWT_WARN_BEFORE_MS =
  minutesFromEnv("VITE_SESSION_JWT_WARN_BEFORE_MINUTES", 3) * 60 * 1000;
